import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Linkedin, MapPin, Mail, Phone,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { submitContact } from "../lib/api";

const CAPS_IMG  = "https://customer-assets.emergentagent.com/job_global-excellence-2/artifacts/s34agds9_1F16CEBD-C0FF-4198-892F-466174E64EF0.png";

/* Client-supplied imagery */
const FACTORY_TEAM_IMG = "https://customer-assets.emergentagent.com/job_global-excellence-2/artifacts/yffshs76_DB46FCC9-B998-4A4B-88D0-6D3FEA6DA9DB.png"; // About — Indian engineers reviewing parts
const CALIPER_IMG      = "https://customer-assets.emergentagent.com/job_global-excellence-2/artifacts/yqsedcuq_IMG_0205.jpeg";                       // Compliance — vernier caliper on lathe
const YOGESH_PORTRAIT  = "https://customer-assets.emergentagent.com/job_global-excellence-2/artifacts/0l45j3rw_IMG_0201.jpeg";                       // Leadership portrait

/* Pexels (industrial) */
const HERO_IMG     = "https://images.pexels.com/photos/8865187/pexels-photo-8865187.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1600&w=2400";
const HERO_VIDEO   = "/hero.mp4";
const ABOUT_IMG    = FACTORY_TEAM_IMG;
const QUALITY_IMG  = "/quality.jpg"; // AI-generated precision CNC photograph (Gemini Nano Banana)
const COMPLY_IMG   = CALIPER_IMG;
const LOGISTICS_IMG= "https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=1100";
const VALUES_IMG   = "https://images.pexels.com/photos/2599244/pexels-photo-2599244.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1600&w=2400";

const EASE = [0.22, 1, 0.36, 1];

/* ============================================================
 * Helpers
 * ============================================================ */
const Heading = ({ children, size = "lg", className = "" }) => {
  const sizes = {
    xl: "text-[44px] md:text-[78px]",
    lg: "text-[36px] md:text-[58px]",
    md: "text-[28px] md:text-[42px]",
  };
  return (
    <h2 className={`font-display ${sizes[size]} leading-[1.02] tracking-[-0.025em] text-[#0A1F44] ${className}`}>
      {children}
    </h2>
  );
};

/* ============================================================
 *  Pillar Badges — used in "Why Choose Us"
 *  Each is a stroked gold icon inside a circular badge frame.
 * ============================================================ */
const Badge = ({ children, "data-testid": testId }) => (
  <span
    data-testid={testId}
    className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#C8961F] bg-white"
    style={{ boxShadow: "inset 0 0 0 4px #FFF, inset 0 0 0 5px rgba(200,150,31,0.20)" }}
  >
    {children}
  </span>
);

const TickGearBadge = () => (
  <Badge data-testid="badge-quality">
    <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none" stroke="#C8961F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 8;
        const x1 = 24 + Math.cos(a) * 16;
        const y1 = 24 + Math.sin(a) * 16;
        const x2 = 24 + Math.cos(a) * 20;
        const y2 = 24 + Math.sin(a) * 20;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
      <circle cx="24" cy="24" r="16" />
      <path d="M16 24 L22 30 L33 18" strokeWidth="2" />
    </svg>
  </Badge>
);

const HandshakeBadge = () => (
  <Badge data-testid="badge-reliable">
    <img
      src="/handshake-gold.png"
      alt="Reliable partner"
      className="h-9 w-9 object-contain"
      draggable="false"
    />
  </Badge>
);

const MoneyBadge = () => (
  <Badge data-testid="badge-cost">
    <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none" stroke="#C8961F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="24" r="14" />
      <path d="M28 18 H22 a3 3 0 0 0 0 6 H26 a3 3 0 0 1 0 6 H20" />
      <line x1="24" y1="14" x2="24" y2="18" />
      <line x1="24" y1="30" x2="24" y2="34" />
    </svg>
  </Badge>
);

const MagnifyBadge = () => (
  <Badge data-testid="badge-customer">
    <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none" stroke="#C8961F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="20" cy="20" r="10" />
      <line x1="28" y1="28" x2="38" y2="38" strokeWidth="2.2" />
      <path d="M16 20 L19 23 L25 17" />
    </svg>
  </Badge>
);

const UpTrendBadge = () => (
  <Badge data-testid="badge-growth">
    <svg viewBox="0 0 48 48" className="h-7 w-7" fill="none" stroke="#C8961F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="38" x2="40" y2="38" />
      <rect x="12" y="28" width="4" height="10" />
      <rect x="20" y="22" width="4" height="16" />
      <rect x="28" y="16" width="4" height="22" />
      <path d="M10 26 L20 18 L26 22 L36 10" strokeWidth="2" />
      <path d="M30 10 L36 10 L36 16" strokeWidth="2" />
    </svg>
  </Badge>
);

const PILLAR_BADGES = [TickGearBadge, HandshakeBadge, MoneyBadge, MagnifyBadge, UpTrendBadge];

/* Thick gold + navy striped section divider — placed between every major section */
const SectionDivider = ({ reverse = false, thin = false }) => (
  <div
    aria-hidden="true"
    className={["section-divider", thin ? "section-divider--thin" : "", reverse ? "section-divider--reverse" : ""].join(" ")}
  />
);

/* Big animated meshing-gears block — used to fill ambient gap between About & Why Choose Us */
const GearsBlock = () => (
  <section className="bg-white py-12 md:py-16" aria-hidden="true">
    <div className="max-w-[1480px] mx-auto px-6 lg:px-12 flex items-center justify-center gap-8 md:gap-14">
      <svg viewBox="0 0 220 220" className="w-[140px] h-[140px] md:w-[200px] md:h-[200px]" fill="none" stroke="#C8961F" strokeWidth="1.4">
        <g className="gear-spin-slow">
          {Array.from({ length: 16 }).map((_, i) => {
            const a = (i * Math.PI * 2) / 16;
            const x1 = 110 + Math.cos(a) * 78;
            const y1 = 110 + Math.sin(a) * 78;
            const x2 = 110 + Math.cos(a) * 96;
            const y2 = 110 + Math.sin(a) * 96;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
          <circle cx="110" cy="110" r="78" />
          <circle cx="110" cy="110" r="55" />
          <circle cx="110" cy="110" r="22" />
        </g>
      </svg>
      <div className="text-center">
        <p className="font-grotesque text-[10px] tracking-[0.42em] uppercase text-[#C8961F] font-bold">Engineered to mesh</p>
        <p className="font-display-italic text-2xl md:text-3xl text-[#0A1F44] mt-3 leading-tight">Every part. Every process.<br />Every time.</p>
      </div>
      <svg viewBox="0 0 180 180" className="w-[110px] h-[110px] md:w-[160px] md:h-[160px]" fill="none" stroke="#3E5BA8" strokeWidth="1.4">
        <g className="gear-spin-rev">
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * Math.PI * 2) / 12;
            const x1 = 90 + Math.cos(a) * 62;
            const y1 = 90 + Math.sin(a) * 62;
            const x2 = 90 + Math.cos(a) * 78;
            const y2 = 90 + Math.sin(a) * 78;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
          })}
          <circle cx="90" cy="90" r="62" />
          <circle cx="90" cy="90" r="40" />
          <circle cx="90" cy="90" r="16" />
        </g>
      </svg>
    </div>
  </section>
);

const Eyebrow = ({ num, children, light = false }) => (
  <p className={["eyebrow", light ? "text-[#E2B548]" : ""].join(" ")}>
    {num && <span className="num" style={light ? { color: "#E2B548" } : {}}>{num}</span>}
    <span>{children}</span>
  </p>
);

/* ============================================================
 *  Blueprint Ornament — subtle gold engineering schematic
 *  Used to fill sparse negative-space with on-brand visuals.
 *  variant: "gear" | "caliper" | "schematic"
 * ============================================================ */
const BlueprintOrnament = ({ variant = "gear", className = "", opacity = 0.09 }) => {
  const stroke = "#C8961F";
  if (variant === "gear") {
    return (
      <svg viewBox="0 0 200 200" aria-hidden="true" className={className} fill="none" stroke={stroke} strokeWidth="1" style={{ opacity }}>
        <circle cx="100" cy="100" r="78" />
        <circle cx="100" cy="100" r="50" />
        <circle cx="100" cy="100" r="22" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * Math.PI * 2) / 12;
          const x1 = 100 + Math.cos(a) * 80;
          const y1 = 100 + Math.sin(a) * 80;
          const x2 = 100 + Math.cos(a) * 96;
          const y2 = 100 + Math.sin(a) * 96;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        <line x1="0" y1="100" x2="200" y2="100" strokeDasharray="2 6" />
        <line x1="100" y1="0" x2="100" y2="200" strokeDasharray="2 6" />
      </svg>
    );
  }
  if (variant === "caliper") {
    return (
      <svg viewBox="0 0 260 140" aria-hidden="true" className={className} fill="none" stroke={stroke} strokeWidth="1" style={{ opacity }}>
        <line x1="10" y1="40" x2="250" y2="40" />
        <line x1="10" y1="100" x2="250" y2="100" />
        {Array.from({ length: 24 }).map((_, i) => (
          <line key={i} x1={20 + i * 10} y1="40" x2={20 + i * 10} y2={i % 5 === 0 ? 26 : 32} />
        ))}
        <rect x="60" y="40" width="60" height="60" />
        <rect x="160" y="40" width="40" height="60" />
        <circle cx="90" cy="70" r="6" />
        <line x1="40" y1="120" x2="220" y2="120" strokeDasharray="3 5" />
      </svg>
    );
  }
  // schematic: pipes & flanges
  return (
    <svg viewBox="0 0 240 160" aria-hidden="true" className={className} fill="none" stroke={stroke} strokeWidth="1" style={{ opacity }}>
      <circle cx="60" cy="80" r="28" />
      <circle cx="60" cy="80" r="14" />
      <line x1="88" y1="80" x2="160" y2="80" />
      <rect x="160" y="60" width="50" height="40" />
      <line x1="210" y1="80" x2="232" y2="80" />
      <line x1="40" y1="20" x2="40" y2="50" strokeDasharray="2 4" />
      <line x1="40" y1="110" x2="40" y2="150" strokeDasharray="2 4" />
      <line x1="125" y1="20" x2="125" y2="60" strokeDasharray="2 4" />
      <line x1="125" y1="100" x2="125" y2="150" strokeDasharray="2 4" />
      <text x="180" y="55" fontSize="6" fill={stroke} stroke="none" letterSpacing="2">SI · ENG</text>
    </svg>
  );
};

/* ============================================================
 * Hero — full-bleed image with overlay text
 * ============================================================ */
const Hero = () => {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    // Belt-and-braces loop — some browsers/tabs throttle background tabs and stop the autoplay loop.
    const handleEnded = () => { v.currentTime = 0; tryPlay(); };
    const handlePause = () => { if (!document.hidden) tryPlay(); };
    const handleVisibility = () => { if (!document.hidden && v.paused) tryPlay(); };
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    v.addEventListener("ended", handleEnded);
    v.addEventListener("pause", handlePause);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
      v.removeEventListener("ended", handleEnded);
      v.removeEventListener("pause", handlePause);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return (
  <section id="home" className="relative min-h-[100vh] flex items-end overflow-hidden bg-[#0A1F44]" data-testid="hero-section">
    {/* Cinematic background video — webm first (Chromium), mp4 fallback (Safari/iOS) */}
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      autoPlay
      muted
      loop
      playsInline
      // eslint-disable-next-line react/no-unknown-property
      webkit-playsinline="true"
      // eslint-disable-next-line react/no-unknown-property
      x5-playsinline="true"
      preload="auto"
      poster={HERO_IMG}
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src="/hero.webm" type="video/webm" />
      <source src={HERO_VIDEO} type="video/mp4" />
    </video>
    {/* Lighter overlays — let imagery breathe; gold/white text stays legible */}
    <div className="absolute inset-0 bg-black/30" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/55 via-[#0A1F44]/15 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/85 via-transparent to-transparent" />

    {/* Editorial corner labels */}
    <div className="absolute top-32 md:top-36 left-6 lg:left-12 text-white z-10">
      <p className="font-grotesque text-[12px] md:text-[14px] tracking-[0.42em] uppercase text-[#E2B548] font-semibold">
        Samarth Infinity · Est. India
      </p>
    </div>
    <div className="absolute top-32 md:top-36 right-6 lg:right-12 text-right z-10">
      <p className="font-grotesque text-[11px] md:text-[12px] tracking-[0.42em] uppercase text-white font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
        Engineering Sourcing &
      </p>
      <p className="font-grotesque text-[11px] md:text-[12px] tracking-[0.42em] uppercase text-white font-semibold mt-1 drop-shadow-[0_1px_8px_rgba(0,0,0,0.6)]">
        Manufacturing Solutions
      </p>
    </div>

    <div className="relative max-w-[1480px] mx-auto w-full px-6 lg:px-12 pb-44 md:pb-48 pt-32 grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-9">
        <motion.h1
          initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease: EASE, delay: 0.3 }}
          className="font-display text-[36px] md:text-[58px] lg:text-[72px] leading-[1.02] tracking-[-0.025em] text-white max-w-4xl"
          data-testid="hero-headline"
        >
          Engineering Sourcing &<br />
          <span className="font-display-italic text-[#E2B548]">Manufacturing Solutions.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
          className="mt-10 max-w-2xl text-white/80 text-base md:text-lg leading-[1.75]"
        >
          Samarth Infinity is an Indian based Engineering Sourcing and Industrial
          Product Supply company — specialising in precision multi-commodity
          manufactured components and integrated supply solutions for OEMs and
          industrial customers worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <a href="#contact" data-testid="hero-cta-primary" className="btn btn--gold">
            Start a project <ArrowRight className="arrow h-4 w-4" />
          </a>
          <a href="#about" data-testid="hero-cta-secondary" className="link-arrow text-white" style={{ color: "#fff" }}>
            <span style={{ color: "#fff" }}>Discover our story</span>
            <ArrowRight className="h-4 w-4 text-[#E2B548]" />
          </a>
        </motion.div>
      </div>
    </div>

    {/* Bottom bar: the 4 cover pillars */}
    <motion.div
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: EASE, delay: 1.0 }}
      className="absolute bottom-0 left-0 right-0 bg-white"
      style={{ borderTop: "5px solid #C8961F", borderBottom: "5px solid #0A1F44" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4">
        {[
          "Global Sourcing Expertise",
          "Wide Range of Manufacturing Capabilities",
          "Quality You Can Trust",
          "On-Time Delivery Everytime",
        ].map((p, i) => (
          <div key={p} className="py-3 md:py-4 px-2 md:px-6 border-l-2 border-[#C8961F]/60 first:border-l-0 even:md:border-l-2">
            <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#C8961F] font-bold">0{i + 1}</p>
            <p className="font-display text-sm md:text-base text-[#0A1F44] mt-1 leading-tight">{p}</p>
          </div>
        ))}
      </div>
    </motion.div>
  </section>
  );
};

/* ============================================================
 *  Marquee Band
 * ============================================================ */
const Marquee = () => (
  <div className="border-y border-[#ECEEF3] overflow-hidden bg-white">
    <div className="flex gap-14 py-5 whitespace-nowrap" style={{ animation: "marq 42s linear infinite" }}>
      {[...Array(2)].map((_, k) => (
        <React.Fragment key={k}>
          {[
            "CNC Machining", "Gravity Die Casting", "High Pressure Die Casting",
            "Low Pressure Die Casting", "Forged Parts", "Fabrication",
            "Rubber Molding", "Surface Treatment",
            "Partnering today. Building tomorrow.",
          ].map((t, i) => (
            <span key={`${k}-${i}`} className="font-display-italic text-2xl md:text-3xl text-[#0A1F44] inline-flex items-center gap-6">
              {t}
              <span className="inline-block w-1.5 h-1.5 bg-[#C8961F] rounded-full" />
            </span>
          ))}
        </React.Fragment>
      ))}
    </div>
    <style>{`@keyframes marq { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
  </div>
);

/* ============================================================
 *  Two-Up Section — alternating image / text
 * ============================================================ */
const TwoUp = ({
  id,
  imageFirst = true,
  image,
  imageCaption,
  eyebrow,
  num,
  title,
  italic,
  body, // array of paragraphs
  cta,
  ctaHref,
  bgSoft = false,
  testId,
  children, // optional extra content below body
}) => {
  return (
    <section id={id} className={`${bgSoft ? "bg-soft" : "bg-white"} py-12 md:py-16`} data-testid={testId}>
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* IMAGE */}
        <div className={`relative lg:col-span-6 ${imageFirst ? "lg:order-1" : "lg:order-2"}`}>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative aspect-[4/5] overflow-hidden bg-[#0A1F44]"
          >
            <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            {/* small caption tag */}
            {imageCaption && (
              <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between text-white">
                <p className="font-display-italic text-xl drop-shadow">{imageCaption}</p>
                <span className="font-grotesque text-[10px] tracking-[0.32em] uppercase font-bold opacity-90">SI ↗</span>
              </div>
            )}
          </motion.div>
        </div>

        {/* TEXT */}
        <div className={`lg:col-span-6 ${imageFirst ? "lg:order-2 lg:pl-6" : "lg:order-1 lg:pr-6"}`}>
          <Reveal>
            <Eyebrow num={num}>{eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <Heading size="lg" className="mt-6">
              {title}
              {italic && (
                <>
                  <br /><span className="font-display-italic text-[#C8961F]">{italic}</span>
                </>
              )}
            </Heading>
          </Reveal>
          <div className="mt-8 space-y-5 text-[#5A6478] text-[17px] leading-[1.8]">
            {body.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.04}><p>{p}</p></Reveal>
            ))}
          </div>
          {children && <Reveal delay={0.2}><div className="mt-10">{children}</div></Reveal>}
          {cta && (
            <Reveal delay={0.25}>
              <a href={ctaHref} className="link-arrow mt-10 inline-flex">{cta} <ArrowRight className="h-3.5 w-3.5 text-[#C8961F]" /></a>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
 *  Pillar Row — 4 / 5 column light pillars
 * ============================================================ */
const PillarRow = ({ items, bgSoft = false }) => (
  <div className={`${bgSoft ? "bg-soft" : "bg-white"} pb-24`}>
    <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-px bg-[#ECEEF3] border border-[#ECEEF3]">
        {items.map((p, i) => (
          <Reveal key={p.k} delay={i * 0.05}>
            <div className={`${bgSoft ? "bg-[#FAFAFA]" : "bg-white"} p-7 h-full`}>
              <p className="font-display-italic text-2xl text-[#C8961F]">{(i + 1).toString().padStart(2, "0")}.</p>
              <h4 className="font-display text-lg text-[#0A1F44] mt-3 leading-tight">{p.k}</h4>
              <p className="text-xs text-[#5A6478] mt-3 leading-[1.7]">{p.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
 *  Capabilities Detail Grid (8 items, verbatim from PDF page 4)
 * ============================================================ */
const CAPS = [
  { n: "01", k: "CNC Machining", d: "Precision machining using advanced CNC technology to deliver complex parts with tight tolerances." },
  { n: "02", k: "Gravity Die Casting", d: "Molten metal is poured into permanent molds by gravity to produce strong, reliable and dimensionally accurate castings." },
  { n: "03", k: "High Pressure Die Casting", d: "Molten metal is injected into the die cavity at high pressure for high precision, excellent surface finish and productivity." },
  { n: "04", k: "Low Pressure Die Casting", d: "Metal is introduced into the die cavity under low pressure for better quality, reduced porosity and improved internal integrity." },
  { n: "05", k: "Forged Parts", d: "High strength forged parts manufactured using precision forging processes for superior performance and durability." },
  { n: "06", k: "Fabrication", d: "Custom fabrication solutions including cutting, welding, assembly and structural works tailored to your specifications." },
  { n: "07", k: "Rubber Molding", d: "High quality rubber components manufactured using advanced molding techniques and premium raw materials." },
  { n: "08", k: "Surface Treatment", d: "Wide range of surface treatment options including coating, plating, anodizing and other finishes to enhance performance and corrosion resistance." },
];

const CapabilitiesGrid = () => (
  <section id="capabilities" className="bg-soft py-12 md:py-16" data-testid="capabilities-section">
    <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
      <Reveal>
        <Eyebrow num="03">Our Engineering &amp; Manufacturing Capabilities</Eyebrow>
      </Reveal>
      <Reveal delay={0.05}>
        <Heading size="lg" className="mt-6 max-w-3xl">
          Integrated solutions through our<br /><span className="font-display-italic text-[#C8961F]">qualified manufacturing partner network.</span>
        </Heading>
      </Reveal>

      {/* Capabilities brand graphic — reduced to a max-width frame for restraint */}
      <Reveal delay={0.1}>
        <div className="mt-12 flex justify-center">
          <div className="max-w-[820px] w-full bg-white p-3 md:p-4 border border-[#ECEEF3]">
            <img src={CAPS_IMG} alt="Samarth Infinity capabilities matrix" className="w-full h-auto block" draggable="false" />
          </div>
        </div>
      </Reveal>

      {/* CAPS DETAIL GRID — thick gold/blue alternating borders between each process row */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2">
        {CAPS.map((c, i) => {
          // Alternate accent: even rows = gold accent, odd = navy accent
          const isGold = i % 2 === 0;
          return (
            <Reveal key={c.k} delay={i * 0.04}>
              <div
                className="bg-white relative p-7 md:p-8 h-full"
                style={{
                  borderTop:    `5px solid ${isGold ? "#C8961F" : "#0A1F44"}`,
                  borderBottom: `5px solid ${isGold ? "#0A1F44" : "#C8961F"}`,
                  borderLeft:   `2px solid ${isGold ? "#C8961F" : "#0A1F44"}`,
                  borderRight:  `2px solid ${isGold ? "#0A1F44" : "#C8961F"}`,
                  marginTop: "-2px",
                  marginLeft: "-1px",
                }}
              >
                <div className="flex items-center gap-4 mb-5">
                  <span className="idx-display">{c.n}</span>
                  <span className={`flex-1 h-[2px] ${isGold ? "bg-[#C8961F]" : "bg-[#0A1F44]"}`} />
                </div>
                <h4 className="font-display text-[22px] text-[#0A1F44] leading-tight" data-testid={`capability-row-${i}`}>{c.k}</h4>
                <p className="text-[15px] text-[#5A6478] leading-[1.8] mt-3 font-light">{c.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============================================================
 *  Leadership Section
 * ============================================================ */
const LeadershipSection = () => (
  <section id="leadership" className="bg-white py-12 md:py-16" data-testid="leadership-section">
    <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      {/* TEXT LEFT */}
      <div className="lg:col-span-7">
        <Reveal><Eyebrow num="07">Leadership Excellence</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <Heading size="lg" className="mt-6">
            A visionary leader.<br />
            <span className="font-display-italic text-[#C8961F]">A strategic partner.</span>
          </Heading>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 text-[#5A6478] text-[17px] leading-[1.8]">
            A Mechanical Engineer with over <span className="text-[#0A1F44] font-semibold">18+ years</span> of extensive
            experience in Strategic Sourcing, End-to-End Procurement, and Material Management across
            Manufacturing & Engineering industries, Mr. Yogesh J. Jadhav brings deep domain expertise,
            commercial acumen, and a results-driven mindset to every engagement.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 border-t border-[#ECEEF3] pt-10">
          {[
            { k: "Strategic Procurement Leadership", d: "Proven ability to lead and build high-performing procurement teams across diverse multi-national organizations, driving efficiency, cost optimization, and value creation." },
            { k: "Strong Industry & Market Exposure", d: "Extensive experience in localization, vendor & component development, and building resilient, sustainable supplier ecosystems." },
            { k: "Commercial Acumen & Strategic Thinking", d: "Skilled in strategic sourcing, negotiation, and risk management with a focus on long-term partnerships and business growth." },
            { k: "Execution Oriented & Result Driven", d: "Strong execution mindset with a passion for process excellence, continuous improvement, and delivering measurable results." },
          ].map((s, i) => (
            <Reveal key={s.k} delay={0.1 + i * 0.05}>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-display-italic text-base text-[#C8961F]">0{i + 1}.</span>
                  <span className="flex-1 h-px bg-[#ECEEF3]" />
                </div>
                <h4 className="font-display text-[19px] text-[#0A1F44] leading-tight">{s.k}</h4>
                <p className="text-sm text-[#5A6478] leading-[1.75] mt-3 font-light">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <p className="mt-12 font-display-italic text-[26px] md:text-[34px] text-[#0A1F44] leading-[1.32] border-l-2 border-[#C8961F] pl-6">
            "Driving excellence in engineering sourcing and manufacturing through
            innovation, collaboration and commitment to customer success."
          </p>
          <p className="mt-4 pl-6 font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#C8961F] font-bold">
            — Yogesh J. Jadhav · Business Head
          </p>
        </Reveal>

        <Reveal delay={0.35}>
          <p className="mt-10 text-[#5A6478] italic">
            Committed to delivering reliable engineering solutions through innovation,
            excellence and continuous improvement.
          </p>
        </Reveal>
      </div>

      {/* PORTRAIT CARD RIGHT */}
      <div className="lg:col-span-5 lg:order-2">
        <Reveal>
          <div className="relative bg-[#0A1F44] p-5">
            {/* gold corner accents on the frame */}
            <span className="absolute top-0 left-0 h-5 w-5 border-t-2 border-l-2 border-[#C8961F]" />
            <span className="absolute top-0 right-0 h-5 w-5 border-t-2 border-r-2 border-[#C8961F]" />
            <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-[#C8961F]" />
            <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-[#C8961F]" />

            {/* portrait */}
            <div className="relative aspect-[4/5] overflow-hidden bg-[#0A1F44]" data-testid="leader-portrait">
              <img
                src={YOGESH_PORTRAIT}
                alt="Yogesh J. Jadhav — Business Head"
                className="absolute inset-0 w-full h-full object-cover object-top"
                draggable="false"
              />
              {/* subtle navy vignette to anchor with site palette */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/55 via-transparent to-transparent" />
            </div>
          </div>

          {/* Identity plaque BELOW the frame */}
          <div className="mt-6 border-l-2 border-[#C8961F] pl-6">
            <p className="font-grotesque text-[10px] tracking-[0.36em] uppercase text-[#C8961F] font-bold">Business Head</p>
            <p className="font-display text-3xl text-[#0A1F44] mt-3">Yogesh J. Jadhav</p>
            <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#5A6478] mt-3 font-bold">
              Mechanical Engineer · 18+ Years
            </p>
            <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#5A6478] mt-1 font-bold">
              Strategic Sourcing · Procurement · Material Management
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <a href="#" data-testid="leader-linkedin" className="mt-8 inline-flex items-center gap-3 font-grotesque text-[12px] tracking-[0.22em] uppercase font-bold text-[#0A1F44] hover:text-[#C8961F] transition-colors">
            <Linkedin className="h-4 w-4" /> Connect on LinkedIn <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ============================================================
 *  Values Section — full-bleed image w/ overlay
 * ============================================================ */
const ValuesSection = () => (
  <section id="values" className="relative py-16 md:py-24 text-white overflow-hidden">
    <img src={VALUES_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/85 to-[#0A1F44]/55" />

    <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12">
      <Reveal>
        <p className="font-grotesque text-[10px] md:text-[11px] tracking-[0.42em] uppercase text-[#E2B548] font-bold">
          Our Values · Our Compass
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-display text-[44px] md:text-[80px] leading-[1.02] tracking-[-0.025em] mt-6 max-w-4xl">
          Partnering today.<br />
          <span className="font-display-italic text-[#E2B548]">Building tomorrow.</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border-t border-b border-white/15">
        {[
          { k: "Integrity", d: "Honest, transparent and accountable in every commitment." },
          { k: "Collaboration", d: "Partnerships built on trust, respect and shared success." },
          { k: "Excellence", d: "Engineering rigor applied through every step of delivery." },
          { k: "Growth", d: "Continuously improving to create long-term value." },
        ].map((v, i) => (
          <Reveal key={v.k} delay={i * 0.05}>
            <div className="bg-[#0A1F44]/40 backdrop-blur-sm p-8 h-full">
              <p className="font-grotesque text-[10px] tracking-[0.36em] uppercase text-[#E2B548] font-bold">0{i + 1}</p>
              <p className="font-display text-3xl mt-5">{v.k}</p>
              <p className="text-sm text-white/70 mt-3 leading-[1.7]">{v.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
 *  Closing band
 * ============================================================ */
const ClosingBand = () => (
  <section className="bg-white py-12 md:py-16 relative overflow-hidden">
    <BlueprintOrnament variant="gear" className="hidden md:block absolute -right-16 top-12 w-[280px] h-[280px] pointer-events-none" opacity={0.07} />
    <BlueprintOrnament variant="schematic" className="hidden md:block absolute -left-12 bottom-12 w-[300px] h-[200px] pointer-events-none" opacity={0.06} />

    <div className="max-w-[1480px] mx-auto px-6 lg:px-12 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <Reveal><Eyebrow num="10">Thank You</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <Heading size="xl" className="mt-6">
              For your time and<br /><span className="font-display-italic text-[#C8961F]">consideration.</span>
            </Heading>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-[#5A6478] text-[18px] leading-[1.85] max-w-2xl">
              We look forward to building a stronger future together — engineering programs
              that scale, qualified partner networks that deliver, and components that hold
              tolerance every single time.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-4 lg:flex lg:justify-end">
          <a href="#contact" className="btn btn--dark">Begin a conversation <ArrowRight className="arrow h-4 w-4" /></a>
        </Reveal>
      </div>

      {/* Promise band — three commitments */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-3 border-t border-[#ECEEF3]">
        {[
          { k: "One business day", d: "Every serious inquiry receives a substantive response from our partner desk within 24 working hours." },
          { k: "Single point of contact", d: "Your program is owned end-to-end by one engineering account lead — no handoffs, no lost context." },
          { k: "Audit-ready paperwork", d: "PPAP, FAI, material test certificates and inspection reports issued with every shipment." },
        ].map((c, i) => (
          <Reveal key={c.k} delay={i * 0.06}>
            <div className="py-8 px-2 md:px-6 border-l border-[#ECEEF3] first:border-l-0 first:pl-0">
              <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#C8961F] font-bold">Our commitment 0{i + 1}</p>
              <p className="font-display text-2xl text-[#0A1F44] mt-4 leading-tight">{c.k}</p>
              <p className="text-sm text-[#5A6478] mt-4 leading-[1.7]">{c.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Footer line — verbatim closing from PDF */}
      <Reveal delay={0.2}>
        <div className="mt-16 border-t border-[#ECEEF3] pt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <p className="font-display-italic text-2xl md:text-3xl text-[#0A1F44] leading-snug">
              "Engineering Sourcing &amp; Manufacturing Solutions —<br />
              <span className="text-[#C8961F]">every part, every process, every time.</span>"
            </p>
          </div>
          <div className="md:col-span-5 md:justify-self-end">
            <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#5A6478] font-bold">Samarth Infinity</p>
            <p className="font-display text-base text-[#0A1F44] mt-2">G301, Phase 3, Pristine Green Society</p>
            <p className="font-display text-base text-[#0A1F44]">Moshi · Pune 412105</p>
            <p className="text-sm text-[#5A6478] mt-1">Engineering programs across global trade lanes.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ============================================================
 *  HOME — composes the full one-scroll site
 * ============================================================ */
const Home = () => {
  return (
    <div data-testid="home-page" className="bg-white">
      <Hero />
      <Marquee />
      <SectionDivider />

      {/* ABOUT — image LEFT */}
      <TwoUp
        id="about"
        testId="about-section"
        num="01"
        eyebrow="About Us"
        title="An Indian engineering"
        italic="sourcing partner."
        image={ABOUT_IMG}
        imageCaption="Precision · India"
        imageFirst={true}
        body={[
          "Samarth Infinity is an Indian based Engineering Sourcing and Industrial Product Supply company specializing in precision multi-commodity manufactured components and integrated supply solutions for OEM's and Industrial Customers worldwide.",
          "With access to a strong network of qualified manufacturing partners across India, we deliver cost-effective, quality-focused engineering components from prototype to production.",
          "We act as a single-point sourcing and supply partner for customers seeking reliable manufacturing solutions from India.",
        ]}
        cta="Why partner with us"
        ctaHref="#why-us"
      />

      <SectionDivider reverse />
      <GearsBlock />
      <SectionDivider />
      {/* WHY CHOOSE US — 5 pillars (PDF page 2) */}
      <section id="why-us" className="bg-soft py-12 md:py-16">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <Reveal><Eyebrow num="02">Why Choose Samarth Infinity</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <Heading size="lg" className="mt-6 max-w-3xl">
              Five principles that define <span className="font-display-italic text-[#C8961F]">every engagement.</span>
            </Heading>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#ECEEF3] border border-[#ECEEF3]">
            {[
              { k: "Quality Assured", d: "Commitment to delivering exceptional quality at every stage." },
              { k: "Reliable Partner", d: "Trusted by OEM's and industrial customers worldwide." },
              { k: "Cost Effective", d: "Delivering value through efficient sourcing and competitive pricing." },
              { k: "Customer Focused", d: "Tailored solutions designed to meet unique customer requirements." },
              { k: "Growth Driven", d: "Continuously improving processes to create long-term value." },
            ].map((p, i) => {
              const BadgeCmp = PILLAR_BADGES[i];
              return (
                <Reveal key={p.k} delay={i * 0.05}>
                  <div className="bg-white p-8 h-full hover:bg-[#0A1F44] hover:text-white transition-colors duration-500 group cursor-default" data-testid={`why-card-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-display-italic text-2xl text-[#C8961F]">0{i + 1}.</p>
                      <BadgeCmp />
                    </div>
                    <h4 className="font-display text-xl mt-5 leading-tight text-[#0A1F44] group-hover:text-white">{p.k}</h4>
                    <p className="text-sm text-[#5A6478] group-hover:text-white/75 mt-4 leading-[1.7]">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <SectionDivider />
      <CapabilitiesGrid />
      <SectionDivider reverse />

      {/* QUALITY ASSURANCE — image LEFT */}
      <TwoUp
        id="quality"
        testId="quality-section"
        num="04"
        eyebrow="Quality Assurance"
        title="Precision. Consistency."
        italic="Confidence."
        image={QUALITY_IMG}
        imageCaption="Quality you can trust"
        imageFirst={true}
        bgSoft={true}
        body={[
          "Quality is not a step in our process — it is the foundation of every program we deliver. From incoming material to final shipment, our quality systems are designed for full traceability, statistical control and audit-ready documentation.",
          "Every part. Every process. Every time.",
        ]}
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-2">
          {[
            "Incoming Inspection",
            "In-process Quality Control",
            "Final Dimensional Inspection",
            "Material Test Certification",
            "CMM Inspection Support",
            "Pressure / Leak Testing",
            "Surface Finish Verification",
            "PPAP / FAI Documentation",
            "Third-party Inspection Coordination",
          ].map((q, i) => (
            <li key={q} className="flex items-center gap-3 py-2 border-b border-[#ECEEF3]" data-testid={`qa-item-${i}`}>
              <CheckCircle2 className="h-4 w-4 text-[#C8961F] shrink-0" />
              <span className="font-grotesque text-[13px] text-[#0A1F44] font-medium">{q}</span>
            </li>
          ))}
        </ul>
      </TwoUp>

      <SectionDivider />

      <PillarRow
        bgSoft
        items={[
          { k: "Stringent Quality Control", d: "Robust checks at every stage." },
          { k: "Precision & Accuracy", d: "Ensuring dimensional excellence." },
          { k: "Documented Processes", d: "Complete traceability & compliance." },
          { k: "Expert Support", d: "Skilled engineers at every step." },
          { k: "Customer Confidence", d: "Delivering reliability you can depend on." },
        ]}
      />

      <SectionDivider reverse />

      {/* COMPLIANCE — text LEFT, image RIGHT */}
      <TwoUp
        id="compliance"
        testId="compliance-section"
        num="05"
        eyebrow="Quality &amp; Compliance Standard"
        title="Committed to excellence."
        italic="Delivered with integrity."
        image={COMPLY_IMG}
        imageCaption="Documented · Traceable"
        imageFirst={false}
        body={[
          "Our compliance posture is built into the way we work. ISO-aligned processes, complete material traceability, and adherence to international quality standards make our deliveries audit-ready by design.",
          "Continuous improvement is not an event — it is a discipline we apply every day.",
        ]}
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {[
            "ISO Compliant Processes",
            "International Quality Standards",
            "Material Traceability",
            "High Precision & Consistency",
            "On-Time Delivery Commitment",
            "Continuous Improvement",
          ].map((q, i) => (
            <li key={q} className="flex items-center gap-3 py-2 border-b border-[#ECEEF3]" data-testid={`compliance-item-${i}`}>
              <CheckCircle2 className="h-4 w-4 text-[#C8961F] shrink-0" />
              <span className="font-grotesque text-[13px] text-[#0A1F44] font-medium">{q}</span>
            </li>
          ))}
        </ul>
      </TwoUp>

      <SectionDivider reverse />

      <PillarRow
        items={[
          { k: "Quality Assured", d: "Consistent quality at every stage." },
          { k: "Compliance Driven", d: "Adhering to global standards." },
          { k: "Documented Processes", d: "Ensuring traceability and accountability." },
          { k: "Customer Focus", d: "Delivering reliable solutions you can depend on." },
          { k: "Continuous Excellence", d: "Improving every day for a better tomorrow." },
        ]}
      />

      <SectionDivider />

      {/* PACKAGING & LOGISTICS — taller image + a LARGE CNC machine traversing right→left */}
      <section id="logistics" className="bg-soft py-12 md:py-16 relative overflow-hidden" data-testid="logistics-section">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LARGER IMAGE LEFT — 7/12 column, taller 3:4 portrait aspect to cover the gap */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative aspect-[3/4] md:aspect-[4/5] overflow-hidden bg-[#0A1F44]"
              style={{ minHeight: "560px" }}
            >
              <img src={LOGISTICS_IMG} alt="Global packaging & logistics" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between text-white">
                <p className="font-display-italic text-2xl drop-shadow">Globally connected</p>
                <span className="font-grotesque text-[10px] tracking-[0.32em] uppercase font-bold opacity-90">SI ↗</span>
              </div>
            </motion.div>

            {/* SHIP ON FLOWING WATER — ship static, water animates infinitely */}
            <div className="mt-8 bg-gradient-to-b from-[#F4F8FE] to-[#DCE6F7] p-5 md:p-8 relative overflow-hidden" style={{ borderTop: "5px solid #C8961F", borderBottom: "5px solid #0A1F44", borderLeft: "2px solid #C8961F", borderRight: "2px solid #0A1F44" }}>
              <div className="flex items-center justify-between mb-5">
                <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#3E5BA8] font-bold">Global Shipping · live</p>
                <span className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#5A6478]">Pune → Worldwide</span>
              </div>

              <div className="relative h-[260px] md:h-[300px] overflow-hidden">
                {/* Sky gradient + faint sun */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#F8FBFF] via-[#E8F1FB] to-[#C7DCF1]" />
                <svg viewBox="0 0 800 60" preserveAspectRatio="none" className="absolute top-2 left-0 w-full h-[60px]" fill="none">
                  <circle cx="660" cy="34" r="22" fill="#FFE7B0" />
                  <circle cx="660" cy="34" r="14" fill="#FFD477" />
                  {/* faint cloud strokes */}
                  <path d="M40 30 q20 -12 40 0 q20 -10 60 0" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.85" />
                  <path d="M180 18 q20 -10 40 0 q15 -8 35 0" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
                  <path d="M500 22 q20 -10 40 0" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
                </svg>

                {/* SHIP — static, centered, brand colors */}
                <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "38%", width: "560px", maxWidth: "92%" }}>
                  <svg viewBox="0 0 560 220" className="w-full h-auto block">
                    {/* === CONTAINER STACKS (foreground deck) === */}
                    {/* Row 1 (bottom) */}
                    {[
                      [120, "#C8961F"], [156, "#0A1F44"], [192, "#5E7FC8"], [228, "#C8961F"],
                      [264, "#0A1F44"], [300, "#5E7FC8"], [336, "#C8961F"], [372, "#0A1F44"],
                    ].map(([x, c], i) => (
                      <rect key={`r1-${i}`} x={x} y="78" width="34" height="22" fill={c} stroke="#0A1F44" strokeWidth="1" />
                    ))}
                    {/* Row 2 (middle) */}
                    {[
                      [138, "#0A1F44"], [174, "#5E7FC8"], [210, "#C8961F"], [246, "#0A1F44"],
                      [282, "#C8961F"], [318, "#5E7FC8"], [354, "#0A1F44"],
                    ].map(([x, c], i) => (
                      <rect key={`r2-${i}`} x={x} y="58" width="34" height="22" fill={c} stroke="#0A1F44" strokeWidth="1" />
                    ))}
                    {/* Row 3 (top) */}
                    {[
                      [156, "#C8961F"], [192, "#0A1F44"], [228, "#C8961F"], [264, "#5E7FC8"],
                      [300, "#0A1F44"], [336, "#C8961F"],
                    ].map(([x, c], i) => (
                      <rect key={`r3-${i}`} x={x} y="38" width="34" height="22" fill={c} stroke="#0A1F44" strokeWidth="1" />
                    ))}
                    {/* Container handle/ridges */}
                    {Array.from({ length: 21 }).map((_, i) => (
                      <line key={`ridge-${i}`} x1={122 + i * 12} y1="78" x2={122 + i * 12} y2="100" stroke="#0A1F44" strokeOpacity="0.35" strokeWidth="0.5" />
                    ))}

                    {/* === HULL === */}
                    {/* Main hull body — long trapezoid */}
                    <path
                      d="M 38 100 L 462 100 L 478 130 L 470 150 L 70 150 L 32 132 Z"
                      fill="#0A1F44" stroke="#03132E" strokeWidth="2"
                    />
                    {/* Gold waterline stripe */}
                    <rect x="32" y="124" width="446" height="6" fill="#C8961F" />
                    {/* Hull rivet line */}
                    <line x1="48" y1="112" x2="464" y2="112" stroke="#FFFFFF" strokeOpacity="0.18" strokeDasharray="3 8" />
                    {/* Anchor port */}
                    <circle cx="62" cy="110" r="3" fill="#C8961F" />
                    <circle cx="62" cy="110" r="1.4" fill="#0A1F44" />
                    {/* Hull name */}
                    <text x="240" y="143" fontSize="9" fill="#FFFFFF" fontWeight="bold" textAnchor="middle" letterSpacing="3" fontFamily="monospace">SAMARTH INFINITY</text>

                    {/* === STERN SUPERSTRUCTURE (bridge tower, right side) === */}
                    <rect x="396" y="14" width="68" height="86" fill="#FFFFFF" stroke="#0A1F44" strokeWidth="1.5" />
                    {/* Bridge windows */}
                    <rect x="404" y="22" width="52" height="10" fill="#5E7FC8" stroke="#0A1F44" strokeWidth="0.8" />
                    <line x1="416" y1="22" x2="416" y2="32" stroke="#0A1F44" strokeWidth="0.6" />
                    <line x1="430" y1="22" x2="430" y2="32" stroke="#0A1F44" strokeWidth="0.6" />
                    <line x1="444" y1="22" x2="444" y2="32" stroke="#0A1F44" strokeWidth="0.6" />
                    {/* Cabin levels */}
                    <line x1="396" y1="44" x2="464" y2="44" stroke="#0A1F44" strokeWidth="0.8" />
                    {Array.from({ length: 6 }).map((_, i) => (
                      <rect key={`p${i}`} x={402 + i * 10} y="48" width="6" height="6" fill="#5E7FC8" />
                    ))}
                    <line x1="396" y1="62" x2="464" y2="62" stroke="#0A1F44" strokeWidth="0.8" />
                    {Array.from({ length: 6 }).map((_, i) => (
                      <rect key={`pp${i}`} x={402 + i * 10} y="66" width="6" height="6" fill="#5E7FC8" />
                    ))}
                    <line x1="396" y1="80" x2="464" y2="80" stroke="#0A1F44" strokeWidth="0.8" />
                    {Array.from({ length: 6 }).map((_, i) => (
                      <rect key={`ppp${i}`} x={402 + i * 10} y="84" width="6" height="6" fill="#5E7FC8" />
                    ))}

                    {/* Funnel / smokestack on the bridge — gold band */}
                    <rect x="420" y="-4" width="24" height="22" fill="#0A1F44" stroke="#03132E" strokeWidth="1.2" />
                    <rect x="420" y="0" width="24" height="6" fill="#C8961F" />

                    {/* Smoke wisps coming from the funnel */}
                    <g className="smoke-drift" opacity="0.55">
                      <path d="M 432 -6 q -8 -14 4 -22 q 12 -8 4 -22" fill="none" stroke="#9FB4D2" strokeWidth="3" strokeLinecap="round" />
                      <path d="M 440 -10 q 6 -10 -2 -18" fill="none" stroke="#B4C5DF" strokeWidth="2" strokeLinecap="round" />
                    </g>

                    {/* Bow mast / antennae */}
                    <line x1="80" y1="100" x2="80" y2="60" stroke="#0A1F44" strokeWidth="1.5" />
                    <line x1="76" y1="62" x2="84" y2="62" stroke="#0A1F44" strokeWidth="1.2" />
                    <line x1="74" y1="66" x2="86" y2="66" stroke="#0A1F44" strokeWidth="1" />
                    <circle cx="80" cy="58" r="2" fill="#C8961F" />

                    {/* Tiny seagull silhouettes */}
                    <path d="M 24 36 q 4 -4 8 0 q 4 -4 8 0" fill="none" stroke="#0A1F44" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M 100 22 q 3 -3 6 0 q 3 -3 6 0" fill="none" stroke="#0A1F44" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M 510 50 q 3 -3 6 0 q 3 -3 6 0" fill="none" stroke="#0A1F44" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </div>

                {/* === ANIMATED WATER LAYERS (low-profile so the ship stays visible) === */}
                {/* Layer 1 — back wave (slow, low opacity) */}
                <div className="absolute left-0 right-0 bottom-0 h-[58px] overflow-hidden">
                  <svg className="wave-back absolute bottom-0" viewBox="0 0 1600 60" preserveAspectRatio="none" style={{ width: "200%", height: "100%", left: 0 }}>
                    <path
                      d="M0 30 Q 100 10  200 30 T 400 30 T 600 30 T 800 30 T 1000 30 T 1200 30 T 1400 30 T 1600 30 V 60 H 0 Z"
                      fill="#5E7FC8" opacity="0.55"
                    />
                  </svg>
                </div>
                {/* Layer 2 — mid wave */}
                <div className="absolute left-0 right-0 bottom-0 h-[48px] overflow-hidden">
                  <svg className="wave-mid absolute bottom-0" viewBox="0 0 1600 50" preserveAspectRatio="none" style={{ width: "200%", height: "100%", left: 0 }}>
                    <path
                      d="M0 24 Q 80 6  160 24 T 320 24 T 480 24 T 640 24 T 800 24 T 960 24 T 1120 24 T 1280 24 T 1440 24 T 1600 24 V 50 H 0 Z"
                      fill="#3E5BA8" opacity="0.8"
                    />
                  </svg>
                </div>
                {/* Layer 3 — front wave (fastest, darkest) */}
                <div className="absolute left-0 right-0 bottom-0 h-[34px] overflow-hidden">
                  <svg className="wave-front absolute bottom-0" viewBox="0 0 1600 36" preserveAspectRatio="none" style={{ width: "200%", height: "100%", left: 0 }}>
                    <path
                      d="M0 16 Q 60 2  120 16 T 240 16 T 360 16 T 480 16 T 600 16 T 720 16 T 840 16 T 960 16 T 1080 16 T 1200 16 T 1320 16 T 1440 16 T 1560 16 T 1600 16 V 36 H 0 Z"
                      fill="#0A1F44"
                    />
                  </svg>
                </div>
                {/* Foam highlights — small gold sparkle line, sits just above the front wave */}
                <div className="absolute left-0 right-0 bottom-[34px] h-[2px] bg-[#C8961F]/30" />
              </div>
            </div>
          </div>

          {/* TEXT RIGHT — 5/12 */}
          <div className="lg:col-span-5">
            <Reveal><Eyebrow num="06">Packaging &amp; Logistic Support</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <Heading size="lg" className="mt-6">
                Carefully packaged. Safely delivered.<br />
                <span className="font-display-italic text-[#C8961F]">Globally connected.</span>
              </Heading>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#5A6478] text-[17px] leading-[1.8]">
                Engineered packaging and orchestrated logistics — protecting the integrity of every shipment from line to last mile, across global trade lanes.
              </p>
            </Reveal>

            <div className="mt-8 space-y-4">
              {[
                { k: "Export Standard Packaging", d: "Reliable packaging as per global export norms." },
                { k: "Custom Packaging Solutions", d: "Tailored packaging to suit your product needs." },
                { k: "Moisture Protection", d: "Advanced protection to ensure product integrity." },
                { k: "Labelling and Marking", d: "Accurate labelling and marking for easy handling and compliance." },
                { k: "Global Shipping Support", d: "Worldwide logistics support through trusted partners." },
                { k: "Timely & Safe Delivery", d: "Committed to on-time delivery with complete safety." },
              ].map((p, i) => (
                <Reveal key={p.k} delay={i * 0.04}>
                  <div className="border-b border-[#ECEEF3] py-3">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="font-display-italic text-sm text-[#C8961F]">0{i + 1}.</span>
                      <span className="flex-1 h-px bg-[#ECEEF3]" />
                    </div>
                    <h4 className="font-display text-[16px] text-[#0A1F44]">{p.k}</h4>
                    <p className="text-xs text-[#5A6478] leading-[1.65] mt-1 font-light">{p.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider reverse />

      <PillarRow
        bgSoft
        items={[
          { k: "Safe & Secure", d: "Products packed with maximum care." },
          { k: "Quality Packaging", d: "Durable packaging for complete protection." },
          { k: "Global Network", d: "Strong logistics network for worldwide reach." },
          { k: "On-Time Delivery", d: "Timely dispatch and delivery you can trust." },
          { k: "Customer Commitment", d: "Your satisfaction is our top priority." },
        ]}
      />

      <SectionDivider />
      <LeadershipSection />

      <SectionDivider reverse />
      <ValuesSection />

      <SectionDivider />
      <ClosingBand />

      <SectionDivider reverse />
      <ContactSection />
    </div>
  );
};

/* ============================================================
 *  Contact Section
 * ============================================================ */
const INITIAL = { name: "", company: "", email: "", phone: "", message: "" };

const ContactSection = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ loading: false, ok: false, err: null });

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, err: null });
    try {
      await submitContact(form);
      setStatus({ loading: false, ok: true, err: null });
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setStatus({ loading: false, ok: false, err: typeof detail === "string" ? detail : "Validation error" });
    }
  };

  return (
    <section id="contact" className="bg-white py-12 md:py-16" data-testid="contact-section">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-12">
          <div className="lg:col-span-7">
            <Eyebrow num="11">Contact</Eyebrow>
            <Heading size="xl" className="mt-6">
              Let's build something<br /><span className="font-display-italic text-[#C8961F]">together.</span>
            </Heading>
          </div>
          <p className="lg:col-span-5 text-[#5A6478] leading-[1.75] text-[17px]">
            We respond to every serious inquiry within one business day. Tell us about
            your program — components, volumes, timelines.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 border-t border-[#ECEEF3] pt-10 mb-16">
          {[
            { icon: MapPin, k: "Office", v: "G301, Phase 3, Pristine Green Society, Moshi, Pune 412105" },
            { icon: Mail, k: "Email", v: "info@samarthinfinity.com" },
            { icon: Phone, k: "Phone", v: "+91 9765406783" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.k}>
                <Icon className="h-4 w-4 text-[#C8961F]" strokeWidth={1.8} />
                <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#5A6478] font-bold mt-4">{c.k}</p>
                <p className="font-display text-lg text-[#0A1F44] mt-2 break-words">{c.v}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="bg-[#C8961F] p-7 md:p-10 relative overflow-hidden">
              <BlueprintOrnament variant="caliper" className="absolute -right-6 -bottom-6 w-[280px] h-[150px] pointer-events-none" opacity={0.16} />
              <p className="font-grotesque text-[10px] tracking-[0.36em] uppercase text-[#0A1F44] font-bold mb-6">Project enquiry · 1 business-day response</p>
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 relative" data-testid="contact-form">
                <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="contact-input-name" />
                <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-input-company" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-input-email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-input-phone" />
                <Field label="Message" name="message" value={form.message} onChange={onChange} required textarea className="md:col-span-2" testId="contact-input-message" />
                <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-6 mt-2">
                  <button type="submit" disabled={status.loading} className="btn btn--dark disabled:opacity-60" data-testid="contact-submit-button">
                    {status.loading ? "Sending…" : "Send message"} <ArrowRight className="arrow h-4 w-4" />
                  </button>
                  {status.ok && (
                    <span className="inline-flex items-center gap-2 text-sm text-[#0A1F44] font-medium" data-testid="contact-success">
                      <CheckCircle2 className="h-4 w-4 text-[#0A1F44]" />
                      Thank you — we will respond within one business day.
                    </span>
                  )}
                  {status.err && <span className="text-sm text-[#7a0000] font-medium" data-testid="contact-error">{status.err}</span>}
                </div>
              </form>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[#ECEEF3] overflow-hidden">
              <iframe
                title="Samarth Infinity — Pune, India"
                src="https://www.google.com/maps?q=Pune%2C+Maharashtra%2C+India&z=11&output=embed"
                className="w-full h-[420px] block grayscale-[35%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
            <div className="mt-6 p-6 bg-soft border border-[#ECEEF3]">
              <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#C8961F] font-bold">Headquarters</p>
              <p className="font-display text-2xl mt-2 text-[#0A1F44]">Pune, Maharashtra · India</p>
              <p className="text-sm text-[#5A6478] mt-3 leading-[1.75]">
                Yogesh J. Jadhav — Business Head. Available across IST business hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", textarea = false, required = false, className = "", testId }) => (
  <label className={["flex flex-col gap-2", className].join(" ")}>
    <span className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-[#0A1F44] font-bold">
      {label} {required && <span className="text-[#0A1F44]">*</span>}
    </span>
    {textarea ? (
      <textarea name={name} value={value} onChange={onChange} required={required} rows={5} data-testid={testId}
        className="bg-white border border-[#A6781A]/40 focus:border-[#0A1F44] outline-none px-4 py-3 text-[#0A1F44] placeholder-slate-400 transition-colors resize-none text-base" />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={testId}
        className="bg-white border border-[#A6781A]/40 focus:border-[#0A1F44] outline-none px-4 py-3 text-[#0A1F44] placeholder-slate-400 transition-colors text-base" />
    )}
  </label>
);

export default Home;
